import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  CircularProgress,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MuiHeader from "../../components/MuiHeader";
import { UploadFileToAzureDB } from "../../components/Azure";
import {
  save_image,
  get_image,
} from "../DashboardSlider/DashboardSlider.thunk";

const UploadBooks = () => {
  const [usertype, setUsertype] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfName, setPdfName] = useState("");
  const [mediaOrder, setMediaOrder] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pdfList, setPdfList] = useState([]);

  const usertype_select_option_list = [
    { value: "fellow", text: "Educator" },
    { value: "school", text: "School" },
    { value: "anganwadi", text: "Anganwadi" },
  ];

  useEffect(() => {
    if (usertype) {
      fetchPdfList(usertype);
    }
  }, [usertype]);

  const fetchPdfList = async (type) => {
    try {
      const datum = {
        usertype: type,
        mediatype: "document",
      };
      const res = await get_image(datum);
      if (res.status === 200) {
        setPdfList(res.data);
      } else {
        toast.error("Failed to fetch uploaded PDFs");
      }
    } catch (err) {
      console.error("Error fetching PDFs:", err);
      toast.error("Error fetching PDF list");
    }
  };

  const handleUsertypeChange = (event) => {
    const selectedType = event.target.value;
    setUsertype(selectedType);
    setPdfFile(null);
    setPdfName("");
    setMediaOrder("");
  };

  const validatePdf = (file) => {
    const maxSize = 5 * 1024 * 1024; // 5 MB
    const fileExtension = file.name.split(".").pop().toLowerCase();
    const fileType = file.type;

    if (file.size > maxSize) {
      toast.error("File size exceeds the maximum allowed (5 MB)");
      return false;
    }

    if (!(fileExtension === "pdf" && fileType === "application/pdf")) {
      toast.error("Only PDF files are allowed");
      return false;
    }

    return true;
  };

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    if (file && validatePdf(file)) {
      setPdfFile(file);
      setPdfName(`uploaded_${new Date().getTime()}.pdf`);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file && validatePdf(file)) {
      setPdfFile(file);
      setPdfName(`uploaded_${new Date().getTime()}.pdf`);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleUpload = async () => {
    if (!pdfFile) {
      toast.error("Please select a PDF file before uploading!");
      return;
    }
    if (!usertype) {
      toast.error("Please select a usertype before uploading!");
      return;
    }
    if (!mediaOrder) {
      toast.error("Please add a unique PDF order!");
      return;
    }

    try {
      setUploading(true);
      await UploadFileToAzureDB(pdfFile, pdfName);

      const body = {
        usertype,
        mediatype: "document", // use valid backend enum
        mediaId: new Date().getTime().toString(),
        mediaName: pdfFile.name,
        mediaUrl: pdfName,
        mediaOrder: parseInt(mediaOrder),
        navigateTo: "",
      };

      const res = await save_image(body);

      if (res.status === 201) {
        toast.success("PDF uploaded successfully!", {
          style: {
            backgroundColor: "green",
            color: "white",
            borderRadius: "30px",
          },
        });

        setPdfFile(null);
        setPdfName("");
        setMediaOrder("");
        const fileInput = document.getElementById("fileInput");
        if (fileInput) fileInput.value = "";

        fetchPdfList(usertype); // refresh list
      } else {
        toast.error("Something went wrong while saving metadata.");
      }
    } catch (err) {
      if (err.response?.status === 500) {
        toast.error("PDF with same order exists. Please try another!", {
          style: {
            backgroundColor: "red",
            color: "white",
            borderRadius: "30px",
          },
        });
      } else if (err.response?.status === 413) {
        toast.error("The file is too large to upload.");
      } else {
        console.error("Upload error:", err);
        toast.error("Something went wrong during upload.");
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <MuiHeader header="Upload Books" />
      <Box sx={{ p: 3 }}>
        <ToastContainer />
        <FormControl sx={{ width: 200, mb: 2 }} size="small">
          <InputLabel id="usertype-label">Usertype</InputLabel>
          <Select
            labelId="usertype-label"
            value={usertype}
            label="Usertype"
            onChange={handleUsertypeChange}
          >
            <MenuItem value="">None</MenuItem>
            {usertype_select_option_list.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.text}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {usertype && (
          <Box>
            <TextField
              label="PDF Order"
              type="number"
              size="small"
              sx={{ mb: 2, width: 200 }}
              value={mediaOrder}
              onChange={(e) => setMediaOrder(e.target.value)}
            />

            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              sx={{
                border: "2px dashed #aaa",
                borderRadius: 4,
                p: 4,
                textAlign: "center",
                mb: 2,
                backgroundColor: dragActive ? "#f0f0f0" : "#fafafa",
                transition: "background-color 0.3s",
                cursor: "pointer",
              }}
            >
              <Typography variant="body1" sx={{ mb: 1 }}>
                Drag & drop PDF here, or click below to select
              </Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
              >
                Select PDF
                <input
                  id="fileInput"
                  type="file"
                  hidden
                  accept="application/pdf"
                  onChange={handleInputChange}
                />
              </Button>
            </Box>

            {pdfFile && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                📄 {pdfFile.name}
              </Typography>
            )}

            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={!pdfFile || uploading}
            >
              {uploading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Upload"
              )}
            </Button>

            {/* Display uploaded PDFs */}
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Uploaded PDFs
              </Typography>
              {pdfList.length === 0 ? (
                <Typography variant="body2">
                  No PDFs found for this usertype.
                </Typography>
              ) : (
                <Paper elevation={1}>
                  <List dense>
                    {pdfList.map((item, index) => (
                      <React.Fragment key={index}>
                        <ListItem>
                          <ListItemText
                            primary={item.mediaName}
                            secondary={`Order: ${item.mediaOrder}`}
                          />
                        </ListItem>
                        <Divider />
                      </React.Fragment>
                    ))}
                  </List>
                </Paper>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export default UploadBooks;
